import buttonCorrect from 'sounds/button_correct.mp3';
import buttonIncorrect from 'sounds/button_incorrect.mp3';
import buttonSelect from 'sounds/button_select.mp3';
import buttonScalingClickOne from 'sounds/button_scaling_click_1.mp3';
import buttonScalingClickTwo from 'sounds/button_scaling_click_2.mp3';
import buttonScalingClickThree from 'sounds/button_scaling_click_3.mp3';
import buttonScalingClickFour from 'sounds/button_scaling_click_4.mp3';
import buttonScalingClickFive from 'sounds/button_scaling_click_5.mp3';
import buttonScalingClickSix from 'sounds/button_scaling_click_6.mp3';

import { type SoundConfig, type SoundConfigKey } from './SoundManager.types';

const soundConfig: SoundConfig = {
    VALIDATE_CORRECT: buttonCorrect,
    VALIDATE_INCORRECT: buttonIncorrect,
    DEFAULT_CLICK: buttonSelect,
    SCALING_CLICKS: [
        buttonScalingClickOne,
        buttonScalingClickTwo,
        buttonScalingClickThree,
        buttonScalingClickFour,
        buttonScalingClickFive,
        buttonScalingClickSix,
    ],
};

// Cache for loaded audio elements
const audioCache = new Map<string, HTMLAudioElement>();

export function resetAudioCache() {
    audioCache.clear();
}

/**
 * Gets the URL for a sound key
 * @param soundKey - Key from SoundConfig
 * @param index - Required index for SCALING_CLICKS array
 * @returns The sound file URL
 */
function getUrlForKey(soundKey: SoundConfigKey, index?: number): string {
    if (soundKey === 'SCALING_CLICKS') {
        if (index === undefined) {
            throw new Error('Index is required for SCALING_CLICKS');
        }
        // Use the provided index, wrapping around if necessary
        const wrappedIndex = index % soundConfig.SCALING_CLICKS.length;
        return soundConfig.SCALING_CLICKS[wrappedIndex];
    }
    return soundConfig[soundKey as keyof Omit<SoundConfig, 'SCALING_CLICKS'>];
}

/**
 * Plays a sound from a URL
 * @param soundUrl - The URL of the sound file to play
 */
function playSoundUrl(soundUrl: string): Promise<void> {
    // Get or create audio element
    let audio = audioCache.get(soundUrl);
    if (!audio) {
        audio = new Audio(soundUrl);
        audio.volume = 0.5; // Default volume
        audioCache.set(soundUrl, audio);
    }

    // Reset playback position and play
    audio.currentTime = 0;
    return audio.play().catch(error => {
        // Silently handle cases where audio playback is blocked
        // oxlint-disable-next-line no-console
        console.warn('Sound playback failed:', error);
    });
}

/**
 * Plays a sound from the sound configuration for SCALING_CLICKS with required index
 * @param soundKey - Must be 'SCALING_CLICKS'
 * @param params - Named parameters object
 * @param params.index - Required index for SCALING_CLICKS array. Plays the sound at the specified
 *                       index (wrapping around if index >= array length). This feature is not currently
 *                       used in the codebase, but is intended for use similar to play_scaling_sound_on_selected.js
 *                       where sounds are played based on answer position/index.
 * @param params.skipInTestMode - Whether to skip playing sound in test mode (default: true)
 */
export async function playSound(
    soundKey: 'SCALING_CLICKS',
    params: { index: number; skipInTestMode?: boolean },
): Promise<void>;

/**
 * Plays a sound from the sound configuration for single URL sound keys
 * @param soundKey - Key from SoundConfig (e.g., 'DEFAULT_CLICK', 'VALIDATE_CORRECT')
 * @param params - Named parameters object
 * @param params.skipInTestMode - Whether to skip playing sound in test mode (default: true)
 */
export async function playSound(
    soundKey: Exclude<SoundConfigKey, 'SCALING_CLICKS'>,
    params?: { skipInTestMode?: boolean },
): Promise<void>;

/**
 * Implementation function - handles both overloaded signatures
 * @param soundKey - Key from SoundConfig
 * @param params - Named parameters object
 * @param params.index - Optional index for SCALING_CLICKS array (required when soundKey is 'SCALING_CLICKS')
 * @param params.skipInTestMode - Whether to skip playing sound in test mode (default: true)
 */
export async function playSound(
    soundKey: SoundConfigKey,
    params: { index?: number; skipInTestMode?: boolean } = {},
): Promise<void> {
    const { index, skipInTestMode = true } = params;
    if (window.RUNNING_IN_TEST_MODE && skipInTestMode) return;

    const soundUrl = getUrlForKey(soundKey, index);
    await playSoundUrl(soundUrl);
}
